import angular from 'angular';

import DeleteOrDeactivateUserCtrl from '../DeleteOrDeactivateUserCtrl';
import deleteUrl from '../delete.html';
import { USERS_LIST_GROUPS_COLLAPSE_THRESHOLD } from '../../../constants';
import config from '../../../../config';

export default class UserUtils {
  constructor(
    Authentication,
    PersonaCollection,
    Restangular,
    extendCalendarModel,
    Utils,
    Users,
    possessiveFilter,
    $uibModal,
    $state
  ) {
    'ngInject';

    Object.assign(this, {
      Authentication,
      PersonaCollection,
      Restangular,
      extendCalendarModel,
      Utils,
      Users,
      possessiveFilter,
      $uibModal,
      $state,
      config,
    });
    this.init();
  }

  init() {
    this.extraUserData = {
      description: this.canAccessUserDescription.bind(this),
    };
  }

  getAvailableGroups(user) {
    const locations = this.Authentication.user.isAdmin
      ? this.PersonaCollection.locations
      : this.PersonaCollection.me.locations;
    return {
      locations: this._filterById(locations, user.locations),
      positions: this._filterById(this.PersonaCollection.positions, user.positions),
      groups: this._filterById(
        this.PersonaCollection.groups,
        user.groups.filter((g) => g.type === 'group')
      ),
    };
  }

  addGroupToUser(user, group, kind) {
    user[kind].push(group);
    return this.getAvailableGroups(user);
  }

  addAllGroups(user, kind) {
    if (kind === 'locations') {
      const locations = this.Authentication.user.isAdmin
        ? this.PersonaCollection.locations
        : this.PersonaCollection.me.locations;
      user.locations = locations.slice();
    } else {
      user[kind] = this.PersonaCollection[kind].slice();
    }

    return this.getAvailableGroups(user);
  }

  clearGroups(user, kind) {
    user[kind].length = 0;
    return this.getAvailableGroups(user);
  }

  removeGroupFromUser(user, group, kind) {
    const index = user[kind].indexOf(group);
    if (index >= 0) {
      user[kind].splice(index, 1);
    }
    return this.getAvailableGroups(user);
  }

  canManageUser(user) {
    if (!this.Authentication.user) {
      return false;
    }

    // user can be undefined if the time entry is assigned to a deleted employee
    // in that case employee cannot be found in personaCollection but it's still attached to time entry entity.
    // https://toasttab.atlassian.net/browse/SMX-847
    if (this.Authentication.user.isAdmin || !user || user.deleted) {
      return true;
    }

    if (!this.Authentication.user.isManager) {
      return false;
    }

    if (this.Authentication.user.managedEmployees?.find(({ id }) => id === user.id)) {
      return true;
    }

    const me = this.PersonaCollection.me;
    return (
      !user.isAdmin &&
      user.locations.some((location) => me.locations.find((l) => l.id === location.id))
    );
  }

  getLocationsForNewUser(user, orgLocations) {
    if (orgLocations.length === 1) {
      return [orgLocations[0]];
    }

    let userLocations = user.locations;
    if (!userLocations.length && user.isAdmin) {
      userLocations = orgLocations;
    }
    const firstAlphabetical = userLocations.slice().sort((a, b) => a.name.localeCompare(b.name))[0];
    return firstAlphabetical ? [firstAlphabetical] : [];
  }

  _filterById(array, existing = []) {
    const existingIds = existing.map((e) => e.id);
    return array.filter((item) => !existingIds.includes(item.id));
  }

  getManageTableRowSize(user, isExpanded = {}, isGroupVisible) {
    // 0, 1, 2 -> 61
    // 3 -> 81
    // 4 -> 101

    const max = (args) => Math.max(...args);
    const count = max(
      [
        'positions',
        'locations',
        'groups' in isExpanded ? 'groups' : null,
        'tags' in isExpanded ? 'tags' : null,
      ]
        .filter((g) => g && (!isGroupVisible || isGroupVisible(g)))
        .map((prop) => {
          let c = user[prop]?.length || 0;
          if (c > USERS_LIST_GROUPS_COLLAPSE_THRESHOLD) {
            c = (isExpanded[prop] ? c : USERS_LIST_GROUPS_COLLAPSE_THRESHOLD) + 1;
          }

          return c;
        })
    );

    if (count <= 2) {
      return 61;
    }

    return 61 + (count - 2) * 20;
  }

  canEditGroup(group) {
    if (this.Authentication.user.isAdmin) {
      return true;
    }

    if (!this.Authentication.user.isManager) {
      return false;
    }

    if (group.type !== 'location') {
      return this.Authentication.user.isManager;
    }

    return this.PersonaCollection.me.locationIds.includes(group.id);
  }

  async getExtraUserData(user) {
    const { data } = await this.Authentication.sendRequest({ path: `users/${user.id}/extra` });

    // HACK - make sure to add any leading zeros if needed to make the PIN always 4-digit
    if (data.kioskPin) {
      data.kioskPin = `0000${data.kioskPin}`.slice(-4);
    }

    return data;
  }

  async updateExtraUserData(user, extra, isChanged = {}) {
    const promises = [];
    const extraData = {};
    Object.entries(this.extraUserData).forEach(async ([field, canAccess]) => {
      if (canAccess(user)) {
        const payload = { [field]: extra[field] };

        if (isChanged[field]) {
          const promise = this.Authentication.sendRequest({
            method: 'put',
            path: `users/${user.id}/${field}`,
            payload,
          });
          promises.push(promise);
          const { data } = await promise;
          Object.assign(extraData, data);
        } else {
          Object.assign(extraData, payload);
        }
      }
    });

    await Promise.all(promises);
    return extraData;
  }

  async regeneratePin(user) {
    try {
      const { data } = await this.Authentication.sendRequest({
        method: 'post',
        path: `kiosks/users/${user.id}/pin`,
      });
      return data.kiosk_pin && data.kiosk_pin !== 'None' ? data.kiosk_pin : null;
    } catch (e) {
      return null;
    }
  }

  canAccessUserDescription(user) {
    // Employees don't have access to descriptions
    if (!this.Authentication.user.isManager) {
      return false;
    }

    // Managers can only see descriptions of employees in their locations
    if (!this.Authentication.user.isAdmin) {
      if (user.isManager) {
        return false;
      }

      if (!this.PersonaCollection.me.locationIds.some((id) => user.locationIds.includes(id))) {
        return false;
      }
    }

    return true;
  }

  canAccessUserPin(user) {
    return user.isMe || this.Authentication.user.isManager;
  }

  canAccessUserLeaveCaps(user) {
    if (
      !this.Authentication.isOrgFeatureEnabled('leaveTypes') ||
      !this.Authentication.user.orgSettings.leaveTypesEnabled
    ) {
      return false;
    }

    if (this.Authentication.user.isAdmin || user.isMe) {
      return true;
    }

    if (!this.Authentication.user.isManager) {
      return false;
    }

    if (user.isManager) {
      return false;
    }

    if (!this.PersonaCollection.me.locationIds.some((id) => user.locationIds.includes(id))) {
      return false;
    }

    return true;
  }

  /**
   * You can always edit your own profile
   * Admins can edit all
   * Managers can edit non-admins
   */
  canEditUser(user) {
    return (
      user.isMe ||
      this.Authentication.user.isAdmin ||
      (this.Authentication.user.isManager && !user.isAdmin)
    );
  }

  /**
   * Admins can edit all
   * Managers can not edit admins and other managers
   */
  canEditUserWages(user) {
    return (
      this.Authentication.user.isAdmin || (this.Authentication.user.isManager && !user.isManager)
    );
  }

  deleteOrDeactivateUser(_user, mode, callback) {
    return this.$uibModal
      .open({
        templateUrl: deleteUrl,
        controllerAs: 'ctrl',
        controller: DeleteOrDeactivateUserCtrl,
        resolve: {
          mode: () => mode,
          user: () => _user,
          users: () => this.PersonaCollection.users.filter((u) => u.id !== _user.id),
        },
      })
      .result.then(callback, angular.noop);
  }

  getUsersSref() {
    const statesToRoutes = {
      dashboard: 'dashboard.manage.users.list',
      users: 'users',
      'labor-cost': 'labor-cost.settings.tabs({tab:"employees"})',
    };
    const state = this.$state.current.name.split('.')[0];
    return statesToRoutes[state];
  }

  getWageDefaultDate(user, positionId = 'base') {
    if (user.wages[positionId]?.length || !user.createdAt) {
      return this.Utils.getNowDate();
    }

    return this.Utils.getRealDate(user.createdAt);
  }

  getSalaryDefaultDate(user) {
    if (user.salaryWages?.length) {
      return this.Utils.getNowDate();
    }

    return this.Utils.getRealDate(user.createdAt);
  }

  isUserLocation(location = {}, users = []) {
    if (users?.length) {
      return users.every(
        (user) =>
          // a deleted user won't be in this.PersonaCollection nor will have locationIds/positionIds himself
          this.PersonaCollection.personasDict[user.id] &&
          this.PersonaCollection.personasDict[user.id].locationIds.includes(location.id)
      );
    }

    return this.PersonaCollection.me.locationIds.includes(location.id);
  }

  isUserPosition(position = {}, users = []) {
    if (users?.length) {
      return users.every(
        (user) =>
          // a deleted user won't be in this.PersonaCollection nor will have locationIds/positionIds himself
          this.PersonaCollection.personasDict[user.id] &&
          this.PersonaCollection.personasDict[user.id].positionIds.includes(position.id)
      );
    }

    return this.PersonaCollection.me.positionIds.includes(position.id);
  }

  async getSession() {
    const sessions = await this.Restangular.all('account').all('sessions').getList();

    if (sessions.length > 0) {
      return sessions[0];
    }
    else {
      return null;
    }
  }

  getLocationsForPersonaPicker(users) {
    const groupBy = '_locationGroup';
    const userLocations = this.PersonaCollection.scheduleLocations
      .filter((location) => this.isUserLocation(location, users))
      .map((location) => {
        const copy = { ...location };
        copy[groupBy] = users?.length ? this.possessiveFilter(users, 'name') : 'Your';
        copy[groupBy] += ' locations';
        return copy;
      });

    const otherLocations = this.PersonaCollection.scheduleLocations
      .filter((location) => !this.isUserLocation(location, users))
      .map((location) => {
        const copy = { ...location };
        copy[groupBy] = userLocations.length ? 'Other locations' : 'Locations';
        return copy;
      });

    return {
      groupBy,
      groupBySort(group = '') {
        return group.indexOf('Other') === 0 ? 1 : 0; // "other" always last
      },
      locations: userLocations.concat(otherLocations),
    };
  }

  getPositionsForPersonaPicker(users) {
    const groupBy = '_positionGroup';
    let positions;
    if (users?.length) {
      const userPositions = this.PersonaCollection.schedulePositions
        .filter((position) => this.isUserPosition(position, users))
        .map((position) => {
          const copy = { ...position };
          copy[groupBy] = `${this.possessiveFilter(users, 'name')} positions`;
          return copy;
        });

      const otherPositions = this.PersonaCollection.schedulePositions
        .filter((position) => !this.isUserPosition(position, users))
        .map((position) => {
          const copy = { ...position };
          copy[groupBy] = userPositions.length ? 'Other positions' : 'Positions';
          return copy;
        });

      positions = userPositions.concat(otherPositions);
    } else {
      positions = this.PersonaCollection.schedulePositions.map((position) => {
        const copy = { ...position };
        copy[groupBy] = 'Positions';
        return copy;
      });
    }

    return {
      groupBy,
      groupBySort(group = '') {
        return group.indexOf('Other') === 0 ? 1 : 0; // "other" always last
      },
      positions,
    };
  }

  getPossibleManagersForUser(user) {
    if (!this.config.MANAGERS_ASSIGNED_TO_USERS_ENABLED) {
      return;
    }

    if (this.Authentication.user.isAdmin) {
      const userLocationIds = user.locations?.map((loc) => loc.id) || [];
      return this.PersonaCollection.users.filter(
        (u) =>
          (u.isAdmin ||
            (u.isManager && u.locations.some((loc) => userLocationIds.includes(loc.id)))) &&
          u.id !== user.id
      );
    }

    return [this.PersonaCollection.me];
  }

  resendVerification() {
    this.Users.all('invite').post([{ id: this.Authentication.user.id }]);
  }
}
